import { Link, createFileRoute } from '@tanstack/react-router'
import { Badge, List, EmptyState, Avatar } from '~/elements'
import { Button, Skeleton } from '@bpinternal/ui-kit-next'
import { Boundary, FilterMenuInput, QueryParamBuilder } from '~/components'
import { auditMessages } from '~/features/audits/components'
import { DateTime } from 'luxon'
import { Color, Variant } from '~/types'
import { Changelog } from '~/assets'
import { ClientOutputs } from '@botpress/client'
import { useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { listAuditsInfiniteQueryOptions } from '~/queries'
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll'
import { useSuspenseQuery } from '~/services'

export const Route = createFileRoute('/workspaces/$workspaceId/settings/audits')({
  validateSearch: (search): { action?: string; userId?: string } => search,
  component: Component,
})

function Component() {
  return (
    <Boundary className="h-96" suspenseFallback={<List items={Array(15).fill(null)}>{<AuditRowSkeleton />}</List>}>
      <Audit />
    </Boundary>
  )
}

const Audit = () => {
  const { workspaceId } = Route.useParams()
  const { action: actionFilter, userId: userIdFilter } = Route.useSearch()
  const {
    data: fetchedAudits,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery(listAuditsInfiniteQueryOptions({ workspaceId }))
  const navigate = Route.useNavigate()

  const workspaceMembers = useSuspenseQuery('workspaces_/$workspaceId_/members', { workspaceId }).data

  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)
  const audits = fetchedAudits?.pages.flatMap((p) => p.records)

  const filters: FilterMenuInput[] = [
    {
      name: 'Action Taken',
      type: 'string',
      paramName: 'action',
      value: Object.entries(auditMessages).map(([value, { label }]) => ({
        name: label,
        value,
        paramName: 'action',
      })),
    },
    {
      name: 'Workspace Member',
      type: 'string',
      paramName: 'userId',
      value: workspaceMembers.map((member) => ({
        name: member.email,
        value: member.userId,
        paramName: 'userId',
      })),
    },
  ]

  const filterdAudits = audits.filter((audit) => {
    let match = true
    if (actionFilter) {
      match = match && audit.action === actionFilter
    }
    if (userIdFilter) {
      match = match && audit.userId === userIdFilter
    }
    return match
  })

  if (audits.length === 0) {
    return (
      <EmptyState
        className="p-8"
        iconSize={6}
        icon={Changelog}
        title="You don't have any audits"
        description="You have not performed any actions that are audited. Keep using Botpress to see your actions here."
        primaryAction={
          <Link to="/workspaces/$workspaceId" params={{ workspaceId }}>
            <Button variant="outline">Back to workspace</Button>
          </Link>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <QueryParamBuilder filters={filters} />
      {filterdAudits.length > 0 ? (
        <>
          <List items={filterdAudits}>{(audit) => <AuditRow record={audit} />}</List>
          <div ref={bottomRef} />
          {isFetching && <AuditRowSkeleton />}
          <Button
            className="mx-auto mt-4"
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={!hasNextPage || isFetching}
          >
            {isFetching ? 'Loading...' : 'Load more items'}
          </Button>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 p-8">
          <span className="text-neutral-900">None of {audits.length} audits matching your filters.</span>
          <div className="flex gap-4">
            {hasNextPage && (
              <Button onClick={() => fetchNextPage()} disabled={isFetching}>
                {isFetching ? 'Loading...' : 'Load more items'}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                void navigate({
                  to: '.',
                  params: (current: Record<string, any>) => current,
                  search: () => ({ action: undefined, userId: undefined }),
                })
              }}
            >
              Clear filters
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

const AuditRow = ({ record }: { record: ClientOutputs['getAuditRecords']['records'][number] }) => {
  const message = auditMessages[record.action] ?? auditMessages.UNKNOWN
  const { workspaceId } = Route.useParams()
  const auditDate = DateTime.fromISO(record.recordedAt).toLocaleString(DateTime.DATETIME_MED)
  const members = useSuspenseQuery('workspaces_/$workspaceId_/members', { workspaceId }).data
  const member = members?.find((member) => member.userId === record.userId)
  return (
    <div className="flex items-center gap-4 p-4">
      <Avatar
        name={member?.displayName ?? member?.email}
        pictureUrl={member?.profilePicture}
        className="h-8 w-8 rounded-sm"
      />
      <div className="flex flex-col gap-1">
        <span className="text-sm text-neutral-900">
          <span className="font-bold">{record.userEmail}</span> {message.render(record)}
        </span>
        <span className="text-xs text-neutral-900">{auditDate}</span>
      </div>
      <Badge className="ml-auto" variant="outline" color={variantToColor(message.variant)}>
        {message.label}
      </Badge>
    </div>
  )
}

const AuditRowSkeleton = () => {
  return (
    <div className="flex items-center gap-4 p-4">
      <Skeleton className="h-8 w-8" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-4 w-44" />
      </div>
      <Skeleton className="ml-auto h-6 w-24" />
    </div>
  )
}

function variantToColor(variant: Variant): Color {
  switch (variant) {
    case 'default':
      return 'gray' as const
    case 'info':
      return 'blue' as const
    case 'success':
      return 'green' as const
    case 'warning':
      return 'yellow' as const
    case 'danger':
      return 'red' as const
    default:
      return 'gray' as const
  }
}
